import { getSensorModeResolution } from '../utilities/getSensorModeResolution';
import { FormState } from '../../types';
import { createResolutionFromValues } from '../utilities/createResolution';
import { getAspectRatio } from '../../../utilities/getAspectRatio';
import { getProtectionResolution } from '../utilities/getProtectionResolution';
import { getFramingDecisionResolution } from '../utilities/getFramingDecisionResolution';
import { percentageToDecimal } from '../../../utilities/percentageToDecimal';
import { getProtectionPercentage } from '../utilities/getProtectionPercentage';
import { FramingChartProps } from './types';
import { getFramingChartElementResolutions } from '../utilities/getFramingChartElementResolutions';
import { calculateAllCoordinates } from './coordinateCalculations';
import { generateAllLabels } from './utils/buildLabelData';
import { formatAspectRatioWithSqueezeFactor } from './utils/textFormatters';
import { getFramingAndWorkingResolutionOutput } from '../CalculatorOutput/getFramingAndWorkingResolutionOutput';
import { FramingAndWorkingResolutionOutputProps } from '../CalculatorOutput/types';
import { Alignment, AspectRatio, Resolution } from '../../../utilities/types';

interface CalculationInputs {
  formValues: FormState;
  desqueezed?: boolean;
}

/**
 * Return type for calculation service - only contains computed/calculated values
 * Form values and UI state should be handled by the calling component
 */
export interface CalculationResults {
  // Computed props for child components
  framingChartProps: FramingChartProps;
  framingAndWorkingResolutionOutput: FramingAndWorkingResolutionOutputProps;
}

/**
 * Core calculation logic extracted from Calculator.tsx and CalculatorOutput.tsx
 */
export function calculateFramingAndWorkingResolution({
  formValues,
  desqueezed,
}: CalculationInputs): CalculationResults | null {
  const {
    directorOfPhotography,
    framingIntentLabel,
    lensSqueezeFactor,
    outputAspectRatio: aspectRatio,
    safetyCrop,
    showTitle,
  } = formValues;

  const sensorMode = formValues.sensorMode?.value;
  if (!sensorMode) {
    return null;
  }

  const effectiveCanvas = createResolutionFromValues(
    formValues.effectiveCanvasWidth,
    formValues.effectiveCanvasHeight
  );
  const customFramingDecision = createResolutionFromValues(
    formValues.customFramingDecisionWidth,
    formValues.customFramingDecisionHeight
  );

  const squeeze = lensSqueezeFactor.value;
  const canvasResolution = getSensorModeResolution(
    sensorMode,
    squeeze,
    desqueezed
  );

  if (!canvasResolution) {
    return null;
  }

  const hasCustomFraming = customFramingDecision !== null;

  let dynamicAspectRatio: AspectRatio = [aspectRatio.value, 1];

  if (hasCustomFraming) {
    try {
      dynamicAspectRatio = getAspectRatio(customFramingDecision, squeeze).raw;
    } catch (error) {
      // If custom framing decision has invalid dimensions, fall back to default aspect ratio
      console.warn('Invalid custom framing decision dimensions:', error);
      return null; // Return null to prevent further calculation errors
    }
  }

  let framingDecisionProtectionResolution: Resolution | null;
  let framingDecision: Resolution | null = null;
  let protection = percentageToDecimal(safetyCrop);

  if (hasCustomFraming) {
    framingDecision = customFramingDecision;

    framingDecisionProtectionResolution =
      canvasResolution != null
        ? getProtectionResolution({
            aspectRatio: dynamicAspectRatio,
            containerResolution: effectiveCanvas || canvasResolution,
            squeeze,
          })
        : null;

    if (framingDecisionProtectionResolution != null) {
      const calculatedProtection = getProtectionPercentage(
        customFramingDecision,
        framingDecisionProtectionResolution
      );
      if (calculatedProtection !== null) {
        protection = percentageToDecimal(calculatedProtection);
      }
    }
  } else {
    framingDecisionProtectionResolution =
      canvasResolution != null
        ? getProtectionResolution({
            aspectRatio: dynamicAspectRatio,
            containerResolution: effectiveCanvas || canvasResolution,
            squeeze,
            desqueezed,
          })
        : null;

    framingDecision =
      framingDecisionProtectionResolution != null
        ? getFramingDecisionResolution({
            containerResolution: framingDecisionProtectionResolution,
            protection,
          })
        : null;

    // Recalculate actual protection percentage after rounding
    if (framingDecision && framingDecisionProtectionResolution) {
      const actualProtectionPercentage = getProtectionPercentage(
        framingDecision,
        framingDecisionProtectionResolution
      );
      if (actualProtectionPercentage !== null) {
        protection = percentageToDecimal(actualProtectionPercentage);
      }
    }
  }

  if (!framingDecision || !framingDecisionProtectionResolution) {
    return null;
  }

  const framingChartElementResolutions = getFramingChartElementResolutions({
    framingDecision,
    framingDecisionProtection: framingDecisionProtectionResolution,
    squeeze,
    desqueezed,
  });

  const coordinates = calculateAllCoordinates({
    alignment: formValues.alignment.value as Alignment,
    canvasResolution,
    effectiveCanvas,
    framingDecisionProtection: framingDecisionProtectionResolution,
    framingDecision,
    testPatternResolution: framingChartElementResolutions.testPatternResolution,
    centerCrossResolution: framingChartElementResolutions.centerCrossResolution,
    logoResolution: framingChartElementResolutions.logoResolution,
    protection,
    squeeze,
    desqueezed,
  });

  const aspectRatioText = formatAspectRatioWithSqueezeFactor(
    dynamicAspectRatio,
    lensSqueezeFactor.value
  );

  const chartText = {
    aspectRatioText,
    framingDecision,
    dop: directorOfPhotography,
    cameraName: sensorMode.cameraName || '',
    sensorModeName: sensorMode.name,
    showTitle,
  };

  const allLabels = generateAllLabels({
    canvasResolution,
    framingDecisionProtection: framingDecisionProtectionResolution,
    framingDecision,
    coordinates,
    effectiveCanvas,
    protection,
  });

  const framingAndWorkingResolutionOutput: FramingAndWorkingResolutionOutputProps =
    getFramingAndWorkingResolutionOutput({
      aspectRatio: aspectRatio.value,
      canvasResolution,
      squeeze,
      protection,
      framingDecisionResolution: framingDecision,
    });

  const framingChartProps: FramingChartProps = {
    aspectRatio: dynamicAspectRatio,
    canvasResolution,
    centerCrossCoordinates: coordinates.centerCrossCoordinates,
    centerCrossResolution: framingChartElementResolutions.centerCrossResolution,
    chartText,
    framingDecisionCoordinates: coordinates.framingDecisionCoordinates,
    framingDecision,
    framingDecisionProtectionCoordinates:
      coordinates.framingDecisionProtectionCoordinates,
    framingDecisionProtection: framingDecisionProtectionResolution,
    framingIntentLabel,
    effectiveCanvasCoordinates: coordinates.effectiveCanvasCoordinates,
    effectiveCanvas,
    lensSqueezeFactor: squeeze,
    logoCoordinates: coordinates.logoCoordinates,
    logoResolution: framingChartElementResolutions.logoResolution,
    protection,
    testPatternCoordinates: coordinates.testPatternCoordinates,
    testPatternResolution: framingChartElementResolutions.testPatternResolution,
    textCoordinates: coordinates.textCoordinates,
    textScale: framingChartElementResolutions.textScale,
    triangleResolution: framingChartElementResolutions.triangleResolution,
    pixelLabels: allLabels.pixelLabels,
    dimensionLabels: allLabels.dimensionLabels,
  };

  const calculationResults: CalculationResults = {
    framingChartProps,
    framingAndWorkingResolutionOutput,
  };

  return calculationResults;
}
